import React, { useMemo } from "react";
import { Alert } from "antd";
import CustomModal from "components/Common/CustomModal";
import { useTranslation } from "react-i18next";
import { getSubjectLabelFromCode } from "utils/schoolUtils";

function dedupeByCodeAndParent(list: any[]) {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const s of list || []) {
    const key = `${s.code}__${s.parent?.id || "no_parent"}`;
    if (!seen.has(key)) {
      seen.add(key);
      out.push(s);
    }
  }
  return out;
}

function groupByParentSubject(items: any[]) {
  const map: Record<string, { parentId: string; parent: any | null; items: any[] }> = {};
  for (const s of items || []) {
    const p = s.parent ?? null;
    const key = p?.id ? String(p.id) : "no_parent";
    if (!map[key]) map[key] = { parentId: key, parent: p, items: [] };
    map[key].items.push(s);
  }
  return Object.values(map).sort((a, b) => {
    if (a.parentId === "no_parent" && b.parentId !== "no_parent") return -1;
    if (a.parentId !== "no_parent" && b.parentId === "no_parent") return 1;
    return (a.parent?.name || "").localeCompare(b.parent?.name || "");
  });
}


interface AssignSubjectsModalProps {
  subjects: any[];
  isModal?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  title?: string;
  className?: string;
}

const AssignSubjectsModal: React.FC<AssignSubjectsModalProps> = ({
  subjects,
  isModal = true,
  isOpen = false,
  onClose,
  title,
  className,
}) => {
  const { t } = useTranslation();

  const grouped = useMemo(() => {
    const clean = dedupeByCodeAndParent(subjects || []);
    return groupByParentSubject(clean);
  }, [subjects]);


  const content = (
    <div className={`mx-auto ${className || ""}`} style={{ maxWidth: 760 }}>
      {!subjects?.length ? (
        <Alert message={t("no_subjects_assigned")} type="info" showIcon />
      ) : (
        <div className="d-flex flex-column gap-4">
          {grouped.map((pg) => (
            <div key={pg.parentId}>
              <div className="d-flex align-items-center pb-2">
                <i
                  className={
                    pg.parentId === "no_parent" ? "ti ti-folders me-2" : "ti ti-books me-2"
                  }
                  style={{ color: "#666", fontSize: 18 }}
                />
                <span className="text-uppercase fw-bold small text-dark text-truncate">
                  {pg.parentId === "no_parent"
                    ? t("base_subjects")
                    : getSubjectLabelFromCode(pg.parent?.code, t) || pg.parent?.name}
                </span>
                <hr className="flex-grow-1 ms-2 my-0" />
              </div>

              {/* Subject rows (white cards, no gray table bg) */}
              <div className="d-flex flex-column gap-2">
                {pg.items.map((s: any, idx: number) => (
                  <div
                    key={`${s.code}-${idx}`}
                    className="d-flex align-items-center justify-content-between border rounded-3 bg-white px-3 py-2 shadow-sm"
                  >
                    <div className="d-flex align-items-center">
                      <span
                        className="me-2"
                        style={{
                          display: "inline-block",
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: s.colorCode || "#ccc",
                        }}
                      />
                      <span className="fw-semibold">
                        {getSubjectLabelFromCode(s.code, t) || s.name}
                      </span>
                    </div>

                    <span className="badge rounded-pill bg-primary">
                      {t("coefficient")}: {s.coefficient}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  if (!isModal) {
    return (
      <section>
        <h6 className="mb-3">{title || t("class_subjects")}</h6>
        {content}
      </section>
    );
  }

  return (
    <CustomModal title={title || t("class_subjects")} isOpen={isOpen} onClose={onClose || (() => {})}>
      {content}
    </CustomModal>
  );
};

export default AssignSubjectsModal;
